import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useArticles } from '../../api/ArticleContext2';
import {
  Layout,
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Row,
  Col,
  Alert,
  Divider,
  Modal,
  Timeline,
  Avatar,
  Breadcrumb,
  Descriptions,
  List,
  Tooltip,
  Result,
  Input,
  Form,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FolderOutlined,
  TagOutlined,
  SendOutlined,
  LikeOutlined,
  MessageOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ArticleDetail.css';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getArticleById, deleteArticle } = useArticles();
  const article = getArticleById(id);

  const [isDeleting, setIsDeleting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([
    {
      id: 1,
      author: '张三',
      avatar: 'https://joeschmoe.io/api/v1/random',
      content: '这是一篇很棒的文章！对我帮助很大。',
      datetime: '1天前',
      likes: 5
    },
    {
      id: 2,
      author: '李四',
      avatar: 'https://joeschmoe.io/api/v1/random',
      content: '感谢作者的分享，期待更多相关主题的文章！',
      datetime: '刚刚',
      likes: 2
    }
  ]);

  // 检查用户是否登录
  const isAuthenticated = () => {
    return !!localStorage.getItem('blog_current_user');
  };

  // 获取当前用户
  const getCurrentUser = () => {
    const user = localStorage.getItem('blog_current_user');
    return user ? JSON.parse(user) : null;
  };

  const handleEdit = () => {
    if (!isAuthenticated()) {
      messageApi.warning('请先登录再编辑文章');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }
    navigate(`/write/${id}`);
  };

  const showDeleteConfirm = () => {
    if (!isAuthenticated()) {
      messageApi.warning('请先登录再删除文章');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    confirm({
      title: '确认删除文章',
      icon: <DeleteOutlined />,
      content: '确定要删除这篇文章吗？此操作不可撤销。',
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      centered: true,
      onOk() {
        return handleDelete();
      },
    });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    messageApi.info('正在删除文章...');

    try {
      await deleteArticle(parseInt(id));
      
      messageApi.success('文章已成功删除！正在跳转到首页...');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      messageApi.error('删除失败，请重试');
      setIsDeleting(false);
    }
  };

  const handleSubmitComment = () => {
    if (!isAuthenticated()) {
      messageApi.warning('请先登录再发表评论');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    if (comment.trim()) {
      const currentUser = getCurrentUser();
      const newComment = {
        id: comments.length + 1,
        author: currentUser.name || currentUser.username,
        avatar: currentUser.avatar || 'https://joeschmoe.io/api/v1/random',
        content: comment,
        datetime: '刚刚',
        likes: 0
      };
      setComments([...comments, newComment]);
      setComment('');
      messageApi.success('评论发表成功！');
    }
  };

  const handleLikeComment = (commentId) => {
    if (!isAuthenticated()) {
      messageApi.warning('请先登录再点赞');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    setComments(comments.map(c => 
      c.id === commentId ? { ...c, likes: c.likes + 1 } : c
    ));
    messageApi.success('点赞成功！');
  };

  if (!article) {
    return (
      <Layout className="article-detail-layout">
        <Content className="article-not-found-content">
          <Result
            status="404"
            title="文章不存在"
            subTitle="抱歉，您访问的文章可能已被删除或不存在。"
            extra={
              <Button 
                type="primary" 
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/')}
              >
                返回首页
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  // 模拟相关文章数据
  const relatedArticles = [
    { id: 1, title: '相关文章一', category: article.category },
    { id: 2, title: '相关文章二', category: article.category },
    { id: 3, title: '相关文章三', category: article.category },
  ];

  return (
    <>
      {contextHolder}
      <Layout className="article-detail-layout">
        <Content className="article-detail-content">
          {/* 面包屑导航 */}
          <Breadcrumb
            items={[
              { title: '首页', onClick: () => navigate('/') },
              { title: '文章列表', onClick: () => navigate('/archive') },
              { title: article.title },
            ]}
            className="article-breadcrumb"
          />

          {/* 主卡片 */}
          <Card className="article-main-card">
            {/* 文章头信息 */}
            <div className="article-header">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div className="article-category-section">
                  <Tag 
                    color="blue" 
                    icon={<FolderOutlined />}
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {article.category}
                  </Tag>
                  <Text type="secondary" style={{ marginLeft: '12px' }}>
                    发布于：{article.date}
                  </Text>
                </div>

                <Title level={1} className="article-title">
                  {article.title}
                </Title>

                <Descriptions
                  column={{ xs: 1, sm: 2, md: 3 }}
                  size="small"
                  className="article-meta"
                >
                  <Descriptions.Item label={<><UserOutlined /> 作者</>}>
                    {article.author}
                  </Descriptions.Item>
                  <Descriptions.Item label={<><CalendarOutlined /> 发布日期</>}>
                    {article.date}
                  </Descriptions.Item>
                  <Descriptions.Item label={<><ClockCircleOutlined /> 阅读时间</>}>
                    {article.readTime}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </div>

            <Divider />

            {/* 文章内容 */}
            <div className="article-content">
              {/* 摘要部分 */}
              <Alert
                message="文章摘要"
                description={article.excerpt}
                type="info"
                showIcon
                className="article-excerpt-alert"
              />

              <div className="article-body">
                <Title level={4} style={{ marginBottom: '24px' }}>
                  正文内容
                </Title>
                
                <Card 
                  className="article-markdown-card"
                  bodyStyle={{ padding: '24px' }}
                >
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({node, ...props}) => <Title level={1} {...props} />,
                      h2: ({node, ...props}) => <Title level={2} {...props} />,
                      h3: ({node, ...props}) => <Title level={3} {...props} />,
                      h4: ({node, ...props}) => <Title level={4} {...props} />,
                      p: ({node, ...props}) => <Paragraph {...props} style={{ marginBottom: '16px' }} />,
                      code: ({node, inline, className, children, ...props}) => 
                        inline ? (
                          <Tag color="geekblue" style={{ fontFamily: 'monospace' }}>
                            {children}
                          </Tag>
                        ) : (
                          <pre 
                            style={{
                              backgroundColor: '#f6f8fa',
                              padding: '16px',
                              borderRadius: '6px',
                              overflow: 'auto',
                              margin: '16px 0'
                            }}
                            {...props}
                          >
                            <code className={className}>{children}</code>
                          </pre>
                        ),
                      ul: ({node, ...props}) => <ul style={{ paddingLeft: '20px', marginBottom: '16px' }} {...props} />,
                      ol: ({node, ...props}) => <ol style={{ paddingLeft: '20px', marginBottom: '16px' }} {...props} />,
                      li: ({node, ...props}) => <li style={{ marginBottom: '8px' }} {...props} />,
                      a: ({node, children, ...props}) => <a style={{ color: '#1890ff' }} {...props}>{children}</a>,
                    }}
                  >
                    {article.content}
                  </ReactMarkdown>
                </Card>
              </div>

              <Divider />

              {/* 标签部分 */}
              <div className="article-tags-section">
                <Space size="small" align="center" style={{ marginBottom: '16px' }}>
                  <TagOutlined style={{ color: '#666' }} />
                  <Text strong>文章标签</Text>
                </Space>
                
                <div className="tags-container">
                  {article.tags.map(tag => (
                    <Tag 
                      key={tag} 
                      color="default"
                      style={{ 
                        fontSize: '14px', 
                        padding: '4px 12px',
                        borderRadius: '16px'
                      }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* 相关文章 */}
              <div className="related-articles">
                <Title level={4} style={{ marginBottom: '16px' }}>
                  相关文章
                </Title>
                
                <Timeline>
                  {relatedArticles.map(item => (
                    <Timeline.Item key={item.id}>
                      <Space direction="vertical" size={0}>
                        <Button 
                          type="link" 
                          onClick={() => navigate(`/article/${item.id}`)}
                          style={{ textAlign: 'left', padding: 0 }}
                        >
                          {item.title}
                        </Button>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.category}
                        </Text>
                      </Space>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            </div>

            {/* 操作按钮 */}
            <Divider />

            <Row justify="space-between" align="middle" className="article-actions">
              <Col>
                <Button 
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  返回列表
                </Button>
              </Col>
              
              <Col>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={handleEdit}
                  >
                    编辑文章
                  </Button>
                  
                  <Button 
                    type="primary" 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={showDeleteConfirm}
                    loading={isDeleting}
                  >
                    删除文章
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 评论区域 */}
          <Card 
            title={
              <Space>
                <MessageOutlined />
                <span>评论 ({comments.length})</span>
              </Space>
            }
            className="article-comments-card"
            style={{ marginTop: '24px' }}
          >
            {/* 评论输入框 */}
            <Space.Compact style={{ width: '100%', marginBottom: '24px' }}>
              <TextArea
                placeholder={isAuthenticated() ? "写下你的评论..." : "请先登录再发表评论"}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
                disabled={!isAuthenticated()}
              />
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                onClick={handleSubmitComment}
                disabled={!comment.trim() || !isAuthenticated()}
              >
                发送
              </Button>
            </Space.Compact>

            {/* 评论列表 */}
            <List
              className="comment-list"
              itemLayout="horizontal"
              dataSource={comments}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button 
                      type="text" 
                      icon={<LikeOutlined />}
                      onClick={() => handleLikeComment(item.id)}
                      disabled={!isAuthenticated()}
                    >
                      {item.likes}
                    </Button>,
                    <Button 
                      type="text" 
                      icon={<MessageOutlined />}
                      disabled={!isAuthenticated()}
                    >
                      回复
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Avatar src={item.avatar} />}
                    title={
                      <Space>
                        <Text strong>{item.author}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <Tooltip title={item.datetime}>
                            <span>{item.datetime}</span>
                          </Tooltip>
                        </Text>
                      </Space>
                    }
                    description={<Paragraph style={{ margin: 0 }}>{item.content}</Paragraph>}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Content>
      </Layout>
    </>
  );
};

export default ArticleDetail;